/**
 * Client-side Edge Config utilities
 * Provides functions for fetching Edge Config values from the API
 */

const API_BASE_URL = '/api/edge-config';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Simple in-memory cache
const cache = new Map();

/**
 * Cache entry structure
 * @typedef {Object} CacheEntry
 * @property {any} value - Cached value
 * @property {number} timestamp - When the value was cached
 */

/**
 * Get cached value if still valid
 * @param {string} key - Cache key
 * @returns {any|null} Cached value or null if expired/not found
 */
function getCached(key) {
    const entry = cache.get(key);
    if (!entry) return null;
    
    const isExpired = Date.now() - entry.timestamp > CACHE_DURATION;
    if (isExpired) {
        cache.delete(key);
        return null;
    }
    
    return entry.value;
}

/**
 * Set value in cache
 * @param {string} key - Cache key
 * @param {any} value - Value to cache
 */
function setCached(key, value) {
    cache.set(key, {
        value,
        timestamp: Date.now(),
    });
}

/**
 * Fetch a single Edge Config value
 * @param {string} key - Edge Config key
 * @param {Object} options - Fetch options
 * @param {boolean} options.useCache - Whether to use cache (default: true)
 * @returns {Promise<any|null>} Config value or null if not found/error
 */
export async function fetchEdgeConfig(key, { useCache = true } = {}) {
    if (!key) {
        console.warn('fetchEdgeConfig: key is required');
        return null;
    }

    // Check cache first
    if (useCache) {
        const cached = getCached(key);
        if (cached !== null) {
            return cached;
        }
    }

    try {
        const response = await fetch(`${API_BASE_URL}?key=${encodeURIComponent(key)}`);
        
        if (!response.ok) {
            console.error(`Failed to fetch Edge Config for key "${key}":`, response.statusText);
            return null;
        }

        const data = await response.json();
        const value = data.value ?? null;

        // Cache the value
        if (useCache && value !== null) {
            setCached(key, value);
        }

        return value;
    } catch (error) {
        console.error(`Error fetching Edge Config for key "${key}":`, error);
        return null;
    }
}

/**
 * Fetch multiple Edge Config values at once (batch request)
 * @param {string[]} keys - Array of Edge Config keys
 * @param {Object} options - Fetch options
 * @param {boolean} options.useCache - Whether to use cache (default: true)
 * @returns {Promise<Record<string, any>>} Object with keys as properties and values from Edge Config
 */
export async function fetchEdgeConfigBatch(keys, { useCache = true } = {}) {
    if (!Array.isArray(keys) || keys.length === 0) {
        return {};
    }

    // Check cache first for all keys
    const cachedResults = {};
    const keysToFetch = [];
    
    keys.forEach((key) => {
        if (useCache) {
            const cached = getCached(key);
            if (cached !== null) {
                cachedResults[key] = cached;
            } else {
                keysToFetch.push(key);
            }
        } else {
            keysToFetch.push(key);
        }
    });

    // If all keys are cached, return cached results
    if (keysToFetch.length === 0) {
        return cachedResults;
    }

    try {
        const response = await fetch(`${API_BASE_URL}/batch`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ keys: keysToFetch }),
        });

        if (!response.ok) {
            // A rate-limited banner request should quietly use the safe default.
            // Retrying four individual keys would only amplify the same limit.
            if (response.status === 429) {
                console.warn('Edge Config banner request was rate limited; using the default navigation.');
                return cachedResults;
            }

            console.error('Failed to fetch Edge Config batch:', response.statusText);
            // Fallback to individual fetches
            const fetchedResults = await fetchEdgeConfigBatchFallback(keysToFetch, { useCache });
            return { ...cachedResults, ...fetchedResults };
        }

        const data = await response.json();
        const fetchedResults = {};

        // Process results and cache them
        keysToFetch.forEach((key) => {
            const value = data.values?.[key] ?? null;
            fetchedResults[key] = value;
            
            if (useCache && value !== null) {
                setCached(key, value);
            }
        });

        return { ...cachedResults, ...fetchedResults };
    } catch (error) {
        console.error('Error fetching Edge Config batch:', error);
        // Fallback to individual fetches
        const fetchedResults = await fetchEdgeConfigBatchFallback(keysToFetch, { useCache });
        return { ...cachedResults, ...fetchedResults };
    }
}

/**
 * Fallback to individual fetches if batch fails
 * @param {string[]} keys - Array of Edge Config keys
 * @param {Object} options - Fetch options
 * @returns {Promise<Record<string, any>>} Object with keys as properties
 */
async function fetchEdgeConfigBatchFallback(keys, options) {
    const results = await Promise.all(
        keys.map(async (key) => ({
            key,
            value: await fetchEdgeConfig(key, options),
        }))
    );

    return results.reduce((acc, { key, value }) => {
        acc[key] = value;
        return acc;
    }, {});
}

/**
 * Clear cache for a specific key or all keys
 * @param {string|null} key - Key to clear, or null to clear all
 */
export function clearEdgeConfigCache(key = null) {
    if (key) {
        cache.delete(key);
    } else {
        cache.clear();
    }
}
